<script lang="ts" setup>
import cloneDeep from 'lodash/cloneDeep'

const props = defineProps({
  allowLastConditionDeletion: {
    default: true,
    type: Boolean,
  },
  attributesToExclude: {
    default: () => [],
    type: Array as PropType<SegmentationAttribute[]>,
  },
  companies: {
    default: undefined,
    type: Array as PropType<CompanyV2[]>,
  },
  endpoint: {
    default: '',
    type: String,
  },
  endpointAttributes: {
    default: undefined,
    type: Object as PropType<SegmentationVerticalEndpointAttributes>,
  },
  gap: {
    default: 'gap-4',
    type: String,
  },
  hasActions: {
    default: true,
    type: Boolean,
  },
  hasBorders: {
    default: false,
    type: Boolean,
  },
  hasQueryPreview: {
    default: false,
    type: Boolean,
  },
  isPreview: {
    default: false,
    type: Boolean,
  },
  modelValue: {
    default: () => [],
    type: Array as PropType<SegmentationCondition[]>,
  },
  updateAiOnType: {
    default: false,
    type: Boolean,
  },
})

const emit = defineEmits<{
  'conditionConfirmed': [condition: SegmentationCondition, conditionIndex: number]
  'conditionUpdated': [condition: SegmentationCondition, conditionIndex: number]
  'search': []
  'update:modelValue': [conditions: SegmentationCondition[]]
}>()

const { createCondition } = useSegmentation()
const { t } = useI18n()
const teamsStore = useTeamsStore()

const currentTab = ref('interface')
const maxConditions = ref(10)

const tabs: UTabItem[] = [
  {
    icon: getIcon('interface'),
    label: t('tabs.interface'),
    value: 'interface',
  },
  {
    icon: getIcon('code'),
    label: t('tabs.code'),
    value: 'code',
  },
]

const finalPath = computed(() => {
  // Generate url
  let path = `${props.endpoint}?`

  // Add query
  if (
    props.modelValue.length
    && !(props.modelValue.length === 1
      && !props.modelValue[0].values.length)
  ) {
    path += `query=${queryForEndpoint.value}`
  }

  // Add attributes
  if (props.endpointAttributes) {
    for (const key in props.endpointAttributes) {
      if (!path.endsWith('?')) {
        path += '&'
      }

      path += `${key}=${props.endpointAttributes[key]}`
    }
  }

  // Append token at the end of the path
  if (!path.endsWith('?')) {
    path += '&'
  }

  path += `token=${teamsStore.apiTokens[0]?.token || 'XXXXXX'}`

  // Remove ending "?"
  if (path.endsWith('?')) {
    path = path.slice(0, -1)
  }

  return path
})

const query = computed({
  get() {
    return props.modelValue
  },

  set(value) {
    emit('update:modelValue', value)
  },
})

const queryForCode = computed(() => {
  return query.value.map((condition) => {
    delete condition.blockedOperator

    return condition
  })
})

const queryForEndpoint = computed(() => {
  const conditions = cloneDeep(query.value)

  for (const condition of conditions) {
    delete condition.blockedOperator
  }

  return encodeURIComponent(JSON.stringify(conditions || '[]'))
})

function onConditionAdded() {
  const condition = createCondition({ query: query.value })

  emit('update:modelValue', [...query.value, condition])
}

function onConditionRemoved(conditionIndex: number) {
  const queryCloned = cloneDeep(query.value)

  queryCloned.splice(conditionIndex, 1)

  emit('update:modelValue', queryCloned)
}

function onConditionUpdated(condition: SegmentationCondition, conditionIndex: number) {
  emit('conditionUpdated', condition, conditionIndex)
}

function onConditionConfirmed(condition: SegmentationCondition, conditionIndex: number) {
  const queryCloned = cloneDeep(props.modelValue)

  queryCloned[conditionIndex] = condition

  emit('update:modelValue', queryCloned)
  emit('conditionConfirmed', condition, conditionIndex)
}

function onTabChange(tabIndex: number) {
  currentTab.value = tabs[tabIndex].value
}
</script>

<template>
  <div class="w-full text-left">
    <div
      class="flex flex-col"
      :class="[gap, { 'shadow-xs overflow-x-auto rounded-lg bg-gray-100 px-4 py-5 ring-1 ring-gray-200 lg:overflow-x-visible dark:bg-gray-900 dark:ring-gray-800 ': hasBorders }]"
    >
      <UTabs
        v-if="hasQueryPreview"
        :items="tabs"
        :ui="{ list: { height: 'h-10', tab: { height: 'h-8', icon: 'size-5', padding: 'px-2', size: 'text-sm' } } }"
        @change="onTabChange"
      />

      <SegmentationVerticalBox
        v-if="currentTab === 'interface'"
        v-model="query"
        :allow-last-condition-deletion="allowLastConditionDeletion"
        :attributes-to-exclude="attributesToExclude"
        :class="{ 'pointer-events-none': isPreview }"
        :companies="companies"
        :has-actions="hasActions"
        :is-preview="isPreview"
        :max-conditions="maxConditions"
        :update-ai-on-type="updateAiOnType"
        @condition-added="onConditionAdded"
        @condition-confirmed="onConditionConfirmed"
        @condition-removed="onConditionRemoved"
        @condition-updated="onConditionUpdated"
      />

      <slot />

      <template v-if="hasQueryPreview">
        <ApiEndpoint
          v-if="currentTab === 'interface'"
          class="mt-1"
          has-borders
          method="GET"
          :path="finalPath"
        />

        <ApiResponse
          v-else-if="currentTab === 'code'"
          :has-header="false"
          :level-to-open="2"
          :response="queryForCode"
        />

        <BaseHighlighter>
          {{ t(`highlight.${currentTab}`) }}
        </BaseHighlighter>
      </template>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "highlight": {
      "code": "The query you must stringify and encode in the URL.",
      "interface": "The endpoint that defines your search criteria."
    },
    "tabs": {
      "code": "Code",
      "interface": "Interface"
    }
  },
  "es": {
    "highlight": {
      "code": "La consulta que debes stringify y codificar en la URL.",
      "interface": "El endpoint que define tus criterios de búsqueda."
    },
    "tabs": {
      "code": "Código",
      "interface": "Interfaz"
    }
  },
  "fr": {
    "highlight": {
      "code": "La requête que vous devez stringifier et encoder dans l'URL.",
      "interface": "L'endpoint qui définit vos critères de recherche."
    },
    "tabs": {
      "code": "Code",
      "interface": "Interface"
    }
  }
}
</i18n>
